// api
import { queries, USERNAME_PLACEHOLDER } from 'RestAPIs/rapini';

// constants
import { tasksGroups } from '../TasksList.const';

// hooks
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQualityMonitorDateRanges } from './useQualityMonitorDateRanges';

// utils
import { getPlanunitCommaIds } from 'components/utils';

// types
import type { TaskGroup, TaskItem } from '../TasksList.types';
import type { PlanUnitMix } from 'components/types';

type UseTasksDataParams = {
  selectedPlanUnits: PlanUnitMix[];
  tilesOrder: string[];
  hasProcessEndOfEmploymentSettlementAccess: boolean;
  hasFeatureToggleExpenseClaims: boolean;
  hasDayLimitViolationsViewAccess: boolean;
  hasAdditionalCompensationsViewAccess: boolean;
  hasHoursPayOutRequestOverview: boolean;
};

// Task groups whose counts all come from the single definitions-count call.
// Everything else is backed by its own endpoint and rendered as its own tile.
const DEFINITION_BACKED_GROUP_KEYS = [
  'flexduty',
  'dutyMutationEmployee',
  'dutyMutationPlanner',
  'dutyRejectedManager',
  'leaveRequests'
] as const;

const applyOrder = (items: TaskGroup[], orderKeys: string[]): TaskGroup[] => {
  if (!orderKeys.length) return items;
  const byKey = new Map(items.map((item) => [item.key, item]));
  const ordered = orderKeys
    .map((key) => byKey.get(key))
    .filter(Boolean) as TaskGroup[];
  const extras = items.filter((item) => !orderKeys.includes(item.key));
  return [...ordered, ...extras];
};

export function useTasksData({
  selectedPlanUnits,
  tilesOrder,
  hasProcessEndOfEmploymentSettlementAccess,
  hasFeatureToggleExpenseClaims,
  hasDayLimitViolationsViewAccess,
  hasAdditionalCompensationsViewAccess,
  hasHoursPayOutRequestOverview
}: UseTasksDataParams) {
  const [data, setData] = useState<TaskGroup[]>([]);

  const orderRef = useRef(tilesOrder);
  useEffect(() => {
    orderRef.current = tilesOrder;
  }, [tilesOrder]);

  const costCenterIds = getPlanunitCommaIds(selectedPlanUnits);

  const {
    startDateDayLimitViolations,
    endDateDayLimitViolations,
    startDateAdditionalCompensations,
    endDateAdditionalCompensations,
    startDateHoursPayOutRequests,
    endDateHoursPayOutRequests
  } = useQualityMonitorDateRanges();

  // No window and no allInboxKeys: the tiles keep the endpoint's own defaults, so
  // one clock decides the range and the grant filter still hides ungranted keys.
  // getTaskCountsWindow only describes that window for the messages hand-off.
  const { data: originalDefinitions } = queries.useGetTaskDefinitionsCount(
    USERNAME_PLACEHOLDER,
    costCenterIds,
    undefined,
    undefined,
    undefined,
    { enabled: selectedPlanUnits.length > 0 }
  );

  const { data: expenseClaimSummaryData, isLoading: isLoadingExpenseClaims } =
    queries.useGetSummary(
      // @ts-ignore
      costCenterIds,
      { enabled: selectedPlanUnits.length > 0 && hasFeatureToggleExpenseClaims }
    );

  const organizationalUnitIdsArray = useMemo<number[]>(
    () =>
      selectedPlanUnits
        ? Array.from(
            new Set(
              selectedPlanUnits
                .map((unit) =>
                  'costCenters' in unit &&
                  Array.isArray(unit.costCenters) &&
                  unit.costCenters?.length > 0
                    ? unit.costCenters.map(({ id }) => id)
                    : unit.id
                )
                .flat()
            )
          )
        : [],
    [selectedPlanUnits]
  );

  const organizationalUnitIds = useMemo(
    () => organizationalUnitIdsArray.join(','),
    [organizationalUnitIdsArray]
  );

  const {
    data: endOfEmploymentSettlementOverviewData,
    isLoading: isLoadingEndOfEmployment
  } = queries.useGetEndOfEmploymentSettlementOverview(
    organizationalUnitIdsArray,
    {
      // Only fetch once a selection is available; otherwise the query would first
      // fire with no cost centers (the authorized-for-all fallback) and then again
      // once selectedPlanUnits arrives.
      enabled:
        !!hasProcessEndOfEmploymentSettlementAccess &&
        organizationalUnitIdsArray.length > 0
    }
  );

  const {
    data: dayLimitViolationsData,
    isLoading: isLoadingDayLimitViolations
  } = queries.useFindDayLimitViolationsByOrganizationalUnits(
    startDateDayLimitViolations.format('YYYY-MM-DD'),
    endDateDayLimitViolations.format('YYYY-MM-DD'),
    // @ts-ignore
    organizationalUnitIds as string[],
    { enabled: hasDayLimitViolationsViewAccess && !!organizationalUnitIds }
  );

  // A count endpoint rather than the overview's own find: the tile needs one number, and building
  // the rows serializes a full duty and employment graph per row - large selections exhausted the
  // JVM String length limit on the data verticle (plans/additional-compensations-oom).
  const {
    data: additionalCompensationsCountData,
    isLoading: isLoadingAdditionalCompensations
  } = queries.useCountAdditionalCompensationsByOrganizationalUnits(
    startDateAdditionalCompensations.format('YYYY-MM-DD'),
    endDateAdditionalCompensations.format('YYYY-MM-DD'),
    // @ts-ignore
    organizationalUnitIds as string[],
    { enabled: hasAdditionalCompensationsViewAccess && !!organizationalUnitIds }
  );

  // A count endpoint rather than the overview's own find: the tile needs one number, and building
  // the rows costs a balance and a nominal duty line lookup per request.
  const {
    data: hoursPayOutRequestCountData,
    isLoading: isLoadingHoursPayOutRequests
  } = queries.useCountHoursPayOutRequestsByOrganizationalUnits(
    startDateHoursPayOutRequests.format('YYYY-MM-DD'),
    endDateHoursPayOutRequests.format('YYYY-MM-DD'),
    // @ts-ignore no paramsSerializer is configured on axios, so the ids have to be sent as a comma
    // separated string instead of an array
    organizationalUnitIds as number[],
    { enabled: hasHoursPayOutRequestOverview && !!organizationalUnitIds }
  );

  useEffect(() => {
    if (!selectedPlanUnits || selectedPlanUnits.length === 0) {
      setData([]);
      return;
    }

    const groups: TaskGroup[] = [];

    // Definitions-backed tiles. These all come from the single definitions
    // count call, so they appear together the moment that (fast) call returns
    // and are simply absent until then — there is no per-tile spinner for them.
    if (originalDefinitions) {
      DEFINITION_BACKED_GROUP_KEYS.forEach((key) => {
        const items = tasksGroups[key]
          .map((definitionKey) =>
            originalDefinitions.find(
              (definition) => definition.definitionKey === definitionKey
            )
          )
          .filter((v): v is TaskItem => v !== undefined);

        if (items.length) groups.push({ key, items });
      });
    }

    // Independent-endpoint tiles. Each is rendered immediately (based on the
    // user's grants) with its own spinner and resolves when its own endpoint
    // responds — decoupled from the definitions call and from each other.
    if (hasProcessEndOfEmploymentSettlementAccess) {
      groups.push({
        key: 'employments',
        loading: isLoadingEndOfEmployment,
        items: [
          {
            definitionKey: 'endOfEmploymentsSettlements',
            count: endOfEmploymentSettlementOverviewData?.length || 0
          }
        ]
      });
    }

    if (hasDayLimitViolationsViewAccess) {
      groups.push({
        key: 'dayLimitViolations',
        loading: isLoadingDayLimitViolations,
        items: [
          {
            definitionKey: 'dayLimitViolations',
            dynamicLabel: 'dayLimitViolationsDynamicLabel',
            date: endDateDayLimitViolations.format('YYYY-MM-DD'),
            count:
              dayLimitViolationsData?.filter(
                (d) => d.canBePaidOut || d.isUnjustifiedPayOut
              )?.length || 0
          }
        ]
      });
    }

    if (hasAdditionalCompensationsViewAccess) {
      groups.push({
        key: 'additionalCompensations',
        loading: isLoadingAdditionalCompensations,
        items: [
          {
            definitionKey: 'additionalCompensations',
            dynamicLabel: 'additionalCompensationsDynamicLabel',
            count: additionalCompensationsCountData?.count || 0
          }
        ]
      });
    }

    if (hasHoursPayOutRequestOverview) {
      groups.push({
        key: 'hoursPayOutRequests',
        loading: isLoadingHoursPayOutRequests,
        items: [
          {
            definitionKey: 'hoursPayOutRequestsOpen',
            count: hoursPayOutRequestCountData?.openRequestCount ?? 0
          }
        ]
      });
    }

    // Travel expense claims uses a different endpoint, so we add it manually.
    if (hasFeatureToggleExpenseClaims) {
      groups.push({
        key: 'travelExpenseClaims',
        loading: isLoadingExpenseClaims,
        items: [
          {
            count:
              expenseClaimSummaryData?.expenseClaimsUnapprovedPreviousMonths ??
              0,
            definitionKey: 'expenseClaimsUnapprovedPreviousMonths'
          },
          {
            count:
              expenseClaimSummaryData?.expenseClaimsUnapprovedCurrentMonth ?? 0,
            definitionKey: 'expenseClaimsUnapprovedCurrentMonth'
          }
        ]
      });
    }

    setData(applyOrder(groups, orderRef.current));
  }, [
    selectedPlanUnits,
    originalDefinitions,
    endOfEmploymentSettlementOverviewData,
    isLoadingEndOfEmployment,
    expenseClaimSummaryData,
    isLoadingExpenseClaims,
    dayLimitViolationsData,
    isLoadingDayLimitViolations,
    additionalCompensationsCountData,
    isLoadingAdditionalCompensations,
    hasProcessEndOfEmploymentSettlementAccess,
    hasDayLimitViolationsViewAccess,
    endDateDayLimitViolations,
    hasAdditionalCompensationsViewAccess,
    hasFeatureToggleExpenseClaims,
    hasHoursPayOutRequestOverview,
    hoursPayOutRequestCountData,
    isLoadingHoursPayOutRequests
  ]);

  return {
    data,
    setData,
    startDateDayLimitViolations,
    endDateDayLimitViolations,
    startDateAdditionalCompensations,
    endDateAdditionalCompensations,
    previousMonthsStartDate: expenseClaimSummaryData?.previousMonthsStartDate,
    previousMonthsEndDate: expenseClaimSummaryData?.previousMonthsEndDate
  };
}
